import React from 'react';
import { Target, Lightbulb, Users, Award, ArrowUpRight } from 'lucide-react';
import { useRevealOnScroll, useParallax } from '../../hooks/useGsap';
import SmartImage from '../ui/SmartImage';

const ValueCard = ({ icon: Icon, title, description, index }) => (
  <div className="reveal group relative bg-night-800 border border-white/[0.07] rounded-3xl p-7 hover:border-primary-400/40 transition-all duration-300 overflow-hidden">
    <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <div className="h-12 w-12 rounded-2xl bg-primary-400 flex items-center justify-center text-night-950 group-hover:rotate-6 transition-transform duration-300">
          <Icon size={22} />
        </div>
        <span className="text-xs font-mono text-mist-600">0{index + 1}</span>
      </div>
      <h3 className="font-display text-2xl font-bold text-mist-50 mb-3 tracking-tight">{title}</h3>
      <p className="text-mist-400 leading-relaxed text-sm">{description}</p>
    </div>
  </div>
);

/* Colagem do "Sobre": um retrato grande à esquerda e duas paisagens
   empilhadas à direita.

   A divisão 46 / 54 não é arbitrária — é ela que faz as duas paisagens
   somadas (mais o gap) fecharem exatamente na altura do retrato, deixando
   cada paisagem em ~16:9. Com a divisão antiga (5/7) elas ficavam em 2,36:1,
   ou seja, achatadas e com metade da altura cortada.
   Se mudar a proporção do retrato (`aspect-[3/4]`), reequilibre a divisão. */
const photoTall = {
  src: '/media/img/equipe-reunida.jpeg',
  label: 'Foto vertical da equipe reunida (retrato)',
};

const photosWide = [
  {
    src: '/media/img/dev-sprint.jpg',
    label: 'Membros desenvolvendo em sprint (paisagem)',
  },
  {
    src: '/media/img/visita tecnica.jpg',
    label: 'Visita técnica ao parceiro (paisagem)',
  },
];

const About = () => {
  const ref = useRevealOnScroll();
  // parallax suave: a coluna da esquerda anda um pouco menos que a da direita.
  // As duas paisagens usam a MESMA força para o vão entre elas não "respirar".
  const imgTall = useParallax(16);
  const imgTop = useParallax(32);
  const imgBottom = useParallax(32);
  const wideRefs = [imgTop, imgBottom];

  const values = [
    {
      icon: Target,
      title: 'Nossa Missão',
      description: 'Resolver desafios reais do agronegócio brasileiro através de soluções tecnológicas de código aberto, conectando o campo ao digital.',
    },
    {
      icon: Lightbulb,
      title: 'Inovação Aberta',
      description: 'Todas as nossas soluções são de código aberto, garantindo transparência e democratização do conhecimento para toda a comunidade.',
    },
    {
      icon: Users,
      title: 'Ecossistema Colaborativo',
      description: 'Funcionamos como um hub de inovação, conectando empresas do agro com estudantes para desenvolver soluções práticas.',
    },
    {
      icon: Award,
      title: 'Excelência Técnica',
      description: 'Aplicamos as mais modernas tecnologias - IoT, Big Data, IA e Agricultura de Precisão - para transformar o agronegócio.',
    },
  ];

  return (
    <section id="about" ref={ref} className="relative py-28 bg-night-900 overflow-hidden">
      {/* Decorative giant text */}
      <div className="absolute top-10 -left-10 pointer-events-none select-none">
        <span className="font-display text-[16rem] font-black text-stroke opacity-[0.04] leading-none">SOBRE</span>
      </div>

      <div className="container-custom relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5">
            <span className="eyebrow reveal">Quem somos</span>
            <h2 className="section-title reveal mt-4">
              A primeira liga estudantil <span className="text-gradient">100% focada em Agro e Tech</span> do Brasil
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <div className="space-y-6 text-mist-300">
              <p className="reveal text-lg leading-relaxed">
                O agronegócio é um dos pilares da economia brasileira, responsável por movimentar
                bilhões de reais anualmente e por colocar o Brasil como um dos maiores produtores
                e exportadores de alimentos do mundo. No entanto, os desafios do setor são tão
                grandes quanto sua importância.
              </p>
              <p className="reveal text-lg leading-relaxed">
                É nesse contexto que o <span className="font-bold text-primary-400">AgroTech Inteli</span> surge
                como uma força que conecta o campo ao digital por meio da tecnologia. Somos a primeira
                liga estudantil 100% focada no desenvolvimento de soluções de tecnologia digital para
                o agronegócio brasileiro.
              </p>
              <p className="reveal text-lg leading-relaxed">
                Funcionamos como um ecossistema de inovação: empresas do agro nos trazem seus desafios,
                e, em parceria com elas, desenhamos o escopo de uma solução. Estruturamos o Termo de
                Abertura de Projeto (TAP), definindo cronograma, sprints e artefatos que serão entregues.
              </p>
              <a href="#cases" className="reveal btn-ghost text-primary-400 font-semibold pt-2">
                Veja como funciona na prática <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Colagem: retrato grande + duas paisagens empilhadas.
            A altura do bloco é governada pela LARGURA dele (altura = largura da
            coluna esquerda ÷ 0,75). Então, para encurtar a colagem sem achatar
            nada, o caminho é estreitar o bloco: `max-w-4xl` ≈ 540px de altura,
            `max-w-5xl` ≈ 615px, sem limite ≈ 710px. */}
        <div className="flex flex-col md:flex-row gap-5 mb-16 md:items-stretch max-w-4xl mx-auto">
          <div
            ref={imgTall}
            className="reveal group w-full md:w-auto md:basis-0 md:grow-[46] aspect-[3/4]"
          >
            <SmartImage
              src={photoTall.src}
              label={photoTall.label}
              className="rounded-3xl h-full w-full border border-white/[0.07]"
              imgClassName="group-hover:scale-105 transition-transform duration-700"
            />
          </div>

          <div className="w-full md:w-auto md:basis-0 md:grow-[54] flex flex-col gap-5">
            {photosWide.map((photo, index) => (
              <div
                key={photo.src}
                ref={wideRefs[index]}
                className="reveal group aspect-[16/9] md:aspect-auto md:flex-1 md:min-h-0"
              >
                <SmartImage
                  src={photo.src}
                  label={photo.label}
                  className="rounded-3xl h-full w-full border border-white/[0.07]"
                  imgClassName="group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((value, index) => (
            <ValueCard key={index} {...value} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
